import { SERVER_URL } from "../../../constants";
import { fetchJsonAuth } from "../utils/http";
import { fetchJson } from "../utils/http";
import { fetchManyWithConcurrency } from "../utils/concurrency";

export const getStudent = (id, token) =>
  fetchJsonAuth(`${SERVER_URL}api/students/${id}`, token);
export const getAdult = (id, token) =>
  fetchJsonAuth(`${SERVER_URL}api/adult/${id}`, token);

export const getStudentsByIds = async (ids, token) => {
  if (!ids?.length) return [];

  const unique = Array.from(new Set(ids));

  const students = await fetchJsonAuth(`${SERVER_URL}api/students/batch`, token, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ ids: unique }),
  });

  // Preserve caller order (IN(...) won’t)
  const byId = new Map((Array.isArray(students) ? students : []).map((s) => [s.id, s]));
  return ids.map((id) => byId.get(id)).filter(Boolean);
};

export const getAdultsByIds = (ids, token) =>
  ids?.length ? fetchManyWithConcurrency(ids, (id) => getAdult(id, token), 6) : Promise.resolve([]);

export const createStudent = (token, body) =>
  fetchJson(`${SERVER_URL}api/students`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });

  export const createStay = (token, studentId, body) =>
  fetchJson(`${SERVER_URL}api/student/${studentId}/stay`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const createStudentsBatch = async (token, bodies, limit = 4) => {
  const results = [];
  await fetchManyWithConcurrency(bodies, async (b) => {
    const created = await createStudent(token, b);
    results.push(created);
  }, limit);
  return results;
};

// GET list of stays for a student
export async function listStaysForStudent(token, studentId) {
  const idNum = Number(studentId);
  if (!token) throw new Error("listStaysForStudent: missing token");
  if (!Number.isInteger(idNum)) throw new Error(`listStaysForStudent: invalid studentId "${studentId}"`);

  const url = `${SERVER_URL}api/student/${encodeURIComponent(idNum)}/stays`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

  if (res.status === 204) return [];             // <- no stays
  if (!res.ok) throw new Error(`GET ${url} -> ${res.status}`);

  let data = await res.json().catch(() => null);
  if (!data) return [];                           // <- null/undefined
  if (Array.isArray(data)) return data;
  return [data];                                  // <- just in case API returns a single object
}

// PUT update a single stay
export const updateStay = (token, studentId, stayId, body) =>
  fetchJson(`${SERVER_URL}api/student/${studentId}/stay/${stayId}`, token, {
    method: "PUT",
    body: JSON.stringify(body),
  });

  export async function listStaysForStudents(token, studentIds) {
  if (!studentIds?.length) return {};

  const unique = Array.from(new Set(studentIds));

  const res = await fetch(`${SERVER_URL}api/students/stays/batch`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ ids: unique }),
  });

  if (!res.ok) throw new Error(`stays/batch -> ${res.status}`);

  const rows = await res.json(); // [{studentId, stayId, campus, arrivalDate, departureDate}, ...]

  const map = {};
  for (const r of rows) {
    if (!map[r.studentId]) map[r.studentId] = [];
    map[r.studentId].push(r);
  }

  return map;
}

export const createAdult = (token, body) =>
  fetchJson(`${SERVER_URL}api/adults`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const createGroupLeader = (token, adultId, body) =>
  fetchJson(`${SERVER_URL}api/groupleader/${adultId}`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const createNsr = (token, adultId, body) =>
  fetchJson(`${SERVER_URL}api/nsrs/${adultId}`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
  
// GET list of NSRs (adult stays) for an adult
export async function listNsrsForAdult(token, adultId) {
  const idNum = Number(adultId);
  if (!token) throw new Error("listNsrsForAdult: missing token");
  if (!Number.isInteger(idNum)) throw new Error(`listNsrsForAdult: invalid adultId "${adultId}"`);

  const url = `${SERVER_URL}api/nsrs/${encodeURIComponent(idNum)}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

  if (res.status === 204) return [];
  if (!res.ok) throw new Error(`GET ${url} -> ${res.status}`);

  const data = await res.json().catch(() => null);
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
}

// PUT update an NSR by its id (note: your UI uses PUT /api/nsrs/{stayId})
export const updateNsr = (token, nsrId, body) =>
  fetchJson(`${SERVER_URL}api/nsrs/${nsrId}`, token, {
    method: "PUT",
    body: JSON.stringify(body),
  });

// Batch full-delete students
export const deleteStudentsBatch = (token, ids) =>
  fetchJson(`${SERVER_URL}api/students/batch-delete`, token, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });

// Batch full-delete adults
export const deleteAdultsBatch = (token, ids) =>
  fetchJson(`${SERVER_URL}api/adults/batch-delete`, token, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });

// Bulk create students + assign to group + optional stays (single transaction)
export const bulkCreateStudents = (token, groupId, { students, stay, groupStartDate, groupEndDate }) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/students/bulk`, token, {
    method: "POST",
    body: JSON.stringify({ students, stay, groupStartDate, groupEndDate }),
  });

// Bulk create adults + assign to group + optional NSR stays (single transaction)
export const bulkCreateAdults = (token, groupId, { adults, nsr, groupStartDate, groupEndDate }) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/adults/bulk`, token, {
    method: "POST",
    body: JSON.stringify({ adults, nsr, groupStartDate, groupEndDate }),
  });

// Align all bookings for a group (residential, weekend, activities, adult weekend) in one call
export const alignBookings = (token, groupId, { studentIds, adultIds, meta }) =>
  fetchJson(`${SERVER_URL}api/campgroup/${groupId}/align-bookings`, token, {
    method: "POST",
    body: JSON.stringify({ studentIds, adultIds, meta }),
  });


